import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Battery, Package, Shield, Fuel, TrendingUp, DollarSign, Leaf, Settings, Briefcase, User } from 'lucide-react';

const PreventeBanner: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    // Date de fin : 15 février 2026
    const endDate = new Date('2026-02-15T23:59:59').getTime();

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = endDate - now;

      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      } else {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const advantages = [
    {
      icon: <Zap size={32} className="text-white" />,
      title: "1er mois gratuit",
      description: "Profitez gratuitement"
    },
    {
      icon: <Battery size={32} className="text-white" />,
      title: "5 recharges incluses",
      description: "Pack de démarrage"
    },
    {
      icon: <Package size={32} className="text-white" />,
      title: "Livraison prioritaire",
      description: "Soyez les premiers"
    },
    {
      icon: <Shield size={32} className="text-white" />,
      title: "Garantie 12 mois",
      description: "Protection totale"
    }
  ];

  const comparison = [
    {
      critere: "Coût mensuel",
      essence: "150 000 FCFA",
      sunu: "45 000 FCFA",
      terranga: "30 000 FCFA"
    },
    {
      critere: "Autonomie",
      essence: "Variable",
      sunu: "90-100 km",
      terranga: "100 km"
    },
    {
      critere: "Vitesse max",
      essence: "80+ km/h",
      sunu: "80 km/h",
      terranga: "45 km/h"
    },
    {
      critere: "Entretien",
      essence: "Élevé",
      sunu: "Minimal",
      terranga: "Minimal"
    },
    {
      critere: "Émission CO₂",
      essence: "Élevée",
      sunu: "0g",
      terranga: "0g"
    }
  ];

  return (
    <section className="relative py-10 sm:py-16 overflow-hidden bg-gradient-to-br from-[#00A859] via-[#0E9F6E] to-[#00A859] animate-gradient-flow">
      {/* Background Pattern animé */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 animate-pulse-slow" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Cercles décoratifs flottants */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-float-slow"></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-accent-300/20 rounded-full blur-3xl animate-float-slower"></div>
      <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-white/5 rounded-full blur-xl animate-float"></div>

      {/* Logo Proxy en haut à droite */}
      <div className="absolute top-4 right-4 sm:top-8 sm:right-8 opacity-30 hover:opacity-50 transition-opacity">
        <img
          src="/proxylogoblanc.png"
          alt="Proxy Emobility"
          className="h-12 sm:h-16 w-auto"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
          }}
        />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative">
        {/* Hero Section */}
        <div className="max-w-5xl mx-auto mb-8">
          {/* Contenu centré */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm font-medium mb-4 animate-shimmer">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-300 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-accent-300"></span>
              </span>
              Offre Limitée
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-3 leading-tight animate-slide-in">
              🔥 Prévente Exclusive <br />
              <span className="text-accent-300 animate-glow">4 Semaines Uniquement !</span>
            </h2>

            <div className="mb-5 space-y-3 max-w-4xl mx-auto">
              <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border-2 border-white/30 transform hover:scale-[1.02] transition-all">
                <div className="flex flex-col sm:flex-row items-center gap-3 justify-center">
                  <span className="inline-flex items-center gap-1.5 bg-blue-600 px-3 py-1.5 rounded-full text-sm font-bold">
                    <Briefcase size={16} />
                    PRO
                  </span>
                  <p className="text-base sm:text-lg text-white font-semibold text-center sm:text-left">
                    SUNU - Pro | <strong className="text-accent-300">1 mois offert + 5 recharges incluses</strong>
                  </p>
                </div>
              </div>

              <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border-2 border-white/30 transform hover:scale-[1.02] transition-all">
                <div className="flex flex-col sm:flex-row items-center gap-3 justify-center">
                  <span className="inline-flex items-center gap-1.5 bg-purple-600 px-3 py-1.5 rounded-full text-sm font-bold">
                    <User size={16} />
                    PARTICULIER
                  </span>
                  <p className="text-base sm:text-lg text-white font-semibold text-center sm:text-left">
                    TERANGA+ - Particuliers | <span className="text-accent-300">Idéal familles, étudiants, trajets urbains</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Compteur */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-5 mb-6 shadow-xl hover:bg-white/15 transition-all max-w-2xl mx-auto">
              <p className="text-white/90 text-sm font-semibold mb-3 text-center">Fin de la prévente dans :</p>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { value: timeLeft.days, label: 'Jours' },
                  { value: timeLeft.hours, label: 'Heures' },
                  { value: timeLeft.minutes, label: 'Minutes' },
                  { value: timeLeft.seconds, label: 'Secondes' }
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-lg p-2 transform hover:scale-105 transition-all shadow-md">
                    <div className="text-xl sm:text-2xl font-bold text-primary-600 animate-number-change">
                      {String(item.value).padStart(2, '0')}
                    </div>
                    <div className="text-xs text-gray-600">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Boutons CTA */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/products/teranga-plus"
                className="inline-flex items-center justify-center px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-primary-600 font-bold rounded-xl hover:bg-accent-300 hover:text-primary-900 transition-all shadow-xl hover:shadow-2xl transform hover:scale-105 group"
              >
                🚀 Réserver ma moto maintenant
                <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-2" />
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center justify-center px-5 sm:px-6 py-2.5 sm:py-3 bg-transparent border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-primary-600 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Découvrir les modèles
              </Link>
            </div>
          </div>
        </div>

        {/* Bloc Avantages 4 colonnes */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 animate-fade-in">
          {advantages.map((advantage, index) => (
            <div
              key={index}
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-3 sm:p-4 text-center hover:bg-white/20 transition-all transform hover:scale-105"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex justify-center mb-2">
                <div className="p-2 bg-white/20 rounded-full">
                  {advantage.icon}
                </div>
              </div>
              <h3 className="text-white font-bold text-sm mb-0.5">{advantage.title}</h3>
              <p className="text-white/70 text-xs">{advantage.description}</p>
            </div>
          ))}
        </div>

        {/* Pourquoi choisir Proxy - Bento Grid Design */}
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-5 sm:p-6 lg:p-8 shadow-2xl">
          <div className="text-center mb-6">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary-900 mb-2">
              Pourquoi choisir <span className="bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent">Proxy</span> ?
            </h3>
            <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto">
              Des avantages concrets qui changent la donne pour votre mobilité
            </p>
          </div>

          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-6">
            {/* Grande Carte 1: Économies - Prend 2 colonnes de hauteur */}
            <div className="md:col-span-6 lg:col-span-5 md:row-span-2 group bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-6 text-white hover:shadow-2xl transition-all transform hover:scale-[1.02] relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
              <div className="relative z-10">
                <div className="inline-flex p-3 bg-white/20 backdrop-blur-sm rounded-xl mb-4 group-hover:rotate-12 transition-transform">
                  <DollarSign size={32} className="text-white" />
                </div>
                <h4 className="text-2xl font-bold mb-3">Économies Massives</h4>
                <div className="text-5xl font-black mb-4 text-accent-300">-70%</div>
                <p className="text-white/90 text-base leading-relaxed">
                  Réduisez vos coûts mensuels de <strong>150k à 45k FCFA</strong> avec Sunu ou <strong>30k FCFA</strong> avec Terranga.
                </p>
                <div className="mt-6 flex items-center gap-2 text-sm">
                  <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full w-[70%] bg-accent-300 rounded-full"></div>
                  </div>
                  <span className="text-accent-300 font-bold">70%</span>
                </div>
              </div>
            </div>

            {/* Petite Carte 1: Autonomie */}
            <div className="md:col-span-6 lg:col-span-3 group bg-gradient-to-br from-amber-50 to-white rounded-2xl p-4 border-2 border-amber-200 hover:border-amber-400 hover:shadow-xl transition-all transform hover:scale-105">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-amber-600 rounded-lg group-hover:rotate-12 transition-transform">
                  <Battery size={24} className="text-white" />
                </div>
                <div className="text-3xl font-bold text-amber-600">100km</div>
              </div>
              <h4 className="text-base font-bold text-amber-900 mb-1">Autonomie</h4>
              <p className="text-gray-600 text-xs">Parfait pour vos trajets quotidiens</p>
            </div>

            {/* Petite Carte 2: Recharge Rapide */}
            <div className="md:col-span-6 lg:col-span-4 group bg-gradient-to-br from-purple-50 to-white rounded-2xl p-4 border-2 border-purple-200 hover:border-purple-400 hover:shadow-xl transition-all transform hover:scale-105">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-purple-600 rounded-lg group-hover:rotate-12 transition-transform">
                  <Zap size={24} className="text-white" />
                </div>
                <div className="text-3xl font-bold text-purple-600">3min</div>
              </div>
              <h4 className="text-base font-bold text-purple-900 mb-1">Recharge Rapide</h4>
              <p className="text-gray-600 text-xs">Swap de batterie ultra-rapide</p>
            </div>

            {/* Grande Carte 2: Zéro Émission */}
            <div className="md:col-span-6 lg:col-span-4 md:row-span-2 group bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-6 text-white hover:shadow-2xl transition-all transform hover:scale-[1.02] relative overflow-hidden">
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
              <div className="relative z-10">
                <div className="inline-flex p-3 bg-white/20 backdrop-blur-sm rounded-xl mb-4 group-hover:rotate-12 transition-transform">
                  <Leaf size={32} className="text-white" />
                </div>
                <h4 className="text-2xl font-bold mb-3">Zéro Émission</h4>
                <div className="text-5xl font-black mb-4 text-green-300">0g CO₂</div>
                <p className="text-white/90 text-base leading-relaxed">
                  100% électrique, 0% pollution. Contribuez à un <strong>avenir durable</strong> pour l'Afrique.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                  <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
                  <span className="text-sm font-semibold">Éco-responsable</span>
                </div>
              </div>
            </div>

            {/* Petite Carte 3: Entretien Minimal */}
            <div className="md:col-span-6 lg:col-span-3 group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-4 border-2 border-blue-200 hover:border-blue-400 hover:shadow-xl transition-all transform hover:scale-105">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-blue-600 rounded-lg group-hover:rotate-12 transition-transform">
                  <Settings size={24} className="text-white" />
                </div>
                <div className="text-3xl font-bold text-blue-600">-85%</div>
              </div>
              <h4 className="text-base font-bold text-blue-900 mb-1">Entretien</h4>
              <p className="text-gray-600 text-xs">Maintenance minimale requise</p>
            </div>

            {/* Grande Carte 3: Garantie */}
            <div className="md:col-span-12 lg:col-span-5 group bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-6 text-white hover:shadow-2xl transition-all transform hover:scale-[1.02] relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
              <div className="relative z-10 flex items-center gap-6">
                <div className="inline-flex p-4 bg-white/20 backdrop-blur-sm rounded-xl group-hover:rotate-12 transition-transform">
                  <Shield size={40} className="text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold mb-2">Garantie Totale</h4>
                  <div className="text-4xl font-black mb-2 text-red-200">2 ans</div>
                  <p className="text-white/90 text-sm">
                    Garantie constructeur complète + <strong>SAV disponible 24/7</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Bannière Économies - Full Width */}
            <div className="md:col-span-12 relative overflow-hidden bg-gradient-to-r from-primary-600 via-primary-500 to-primary-600 rounded-2xl p-4 text-white shadow-xl">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0 animate-pulse-slow" style={{
                  backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 1px)`,
                  backgroundSize: '30px 30px'
                }} />
              </div>
              <div className="relative z-10 flex flex-col sm:flex-row items-center justify-center gap-3 text-center sm:text-left">
                <TrendingUp size={28} className="animate-bounce flex-shrink-0" />
                <p className="text-base sm:text-lg font-bold">
                  Économisez jusqu'à <span className="text-accent-300">105 000 FCFA par mois</span> en passant à l'électrique avec Proxy Emobility
                </p>
              </div>
            </div>
          </div>

          {/* Notice en bas */}
          <p className="text-center text-gray-600 text-xs italic">
            Prévente valable jusqu'au <strong className="text-primary-600">15 Février 2026</strong> dans la limite des stocks disponibles.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes gradient-flow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        @keyframes float-slow {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(20px, -30px); }
        }

        @keyframes float-slower {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-30px, 20px); }
        }

        @keyframes shimmer {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes glow {
          0%, 100% { text-shadow: 0 0 20px rgba(245, 158, 11, 0.5); }
          50% { text-shadow: 0 0 40px rgba(245, 158, 11, 0.8); }
        }

        @keyframes pulse-slow {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.15; }
        }

        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        .animate-gradient-flow {
          background-size: 200% 200%;
          animation: gradient-flow 15s ease infinite;
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }

        .animate-fade-in {
          animation: fade-in 0.6s ease-out forwards;
        }

        .animate-slide-in {
          animation: slide-in 0.8s ease-out;
        }

        .animate-glow {
          animation: glow 2s ease-in-out infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }

        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }

        .animate-float-slower {
          animation: float-slower 10s ease-in-out infinite;
        }

        .animate-bounce-subtle {
          animation: bounce-subtle 2s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default PreventeBanner;
